import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Typography,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Menu,
  MenuItem,
  Checkbox,
  LinearProgress
} from "@mui/material";
import Swal from "sweetalert2";
import io from "socket.io-client";
import sampleQrImage from "../../assets/sampleQrImage.png";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  WhatsApp as WhatsAppIcon,
  QrCode as QrCodeIcon,
  Refresh as RefreshIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  MoreVert as MoreVertIcon,
  Message as MessageIcon,
  Phone as PhoneIcon,
  Group as GroupIcon,
  Star as StarIcon,
  ClearAll as ClearIcon,
  RestoreFromTrash as RestoreIcon,
  Security as SecurityIcon,
  PowerSettingsNew as PowerIcon,
  Chat as ChatIcon
} from "@mui/icons-material";
import {
  getAllCustomerGroups,
  createCustomerGroup,
  updateCustomerGroup,
  deleteCustomerGroup,
  bulkUpdateCustomerGroups,
  getAllWhatsAppStatus,
  initializeWhatsAppGroup,
  sendWhatsAppGroupMessage,
  restartWhatsAppGroup,
  destroyWhatsAppGroup,
  setDefaultInstance as setDefaultInstanceAPI,
  getDefaultInstance,
  // Session management functions
  clearSessionData,
  completeReinitialization,
  recoverAuthentication,
  forceReinitialize
} from "../../api/customerGroup";
import { requestGroupQR } from "../../api/whatsapp";
import { useNavigate } from "react-router-dom";
import {
  BG_WHITE,
  BORDER,
  BORDER_PURPLE,
  RADIUS_CARD,
  CARD_SHADOW,
  PURPLE_MAIN,
  PURPLE_DARK,
  BG_PURPLE_GRADIENT,
  TEXT_BODY,
} from "components/bento/bentoTokens";

const WhatsAppInstance = () => {
  const navigate = useNavigate();

  // State management
  const [groups, setGroups] = useState([]);
  const [whatsappStatus, setWhatsappStatus] = useState({});
  const [defaultInstance, setDefaultInstance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openQRDialog, setOpenQRDialog] = useState(false);
  const [openMessageDialog, setOpenMessageDialog] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [qrData, setQrData] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedMenuGroupId, setSelectedMenuGroupId] = useState(null);
  const [alert, setAlert] = useState({ show: false, message: "", severity: "info" });

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    whatsappAPI: "",
    whatsappInstanceName: "",
    maxConcurrentMessages: 10,
    remark: "",
    isActive: true
  });

  // Message dialog state
  const [messageData, setMessageData] = useState({
    groupId: null,
    phoneNumber: "",
    message: ""
  });
  const [sendingMessage, setSendingMessage] = useState(false);
  const [initializingGroups, setInitializingGroups] = useState({});

  // QR Code countdown and loading state
  const [qrCountdown, setQrCountdown] = useState(0); // Countdown timer in seconds
  const [qrMaxTimeout, setQrMaxTimeout] = useState(20); // Initial timeout value for progress calculation
  const [qrLoading, setQrLoading] = useState(false); // Loading state for QR generation after restart
  const socketRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const qrTimeoutRef = useRef(null); // Timeout for waiting on socket QR delivery
  const qrDataRef = useRef(null); // Ref to track QR data for polling checks
  const qrPendingRef = useRef(false); // true while the QR dialog waits for its first QR (read by the timeout)

  // Load data on component mount
  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      await Promise.all([loadGroups(), loadWhatsAppStatus(), loadDefaultInstance()]);
      setLoading(false);
    };

    loadInitialData();
    // Set up periodic refresh for WhatsApp status
    const interval = setInterval(loadWhatsAppStatus, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    setLoading(true);
    await Promise.all([loadGroups(), loadWhatsAppStatus(), loadDefaultInstance()]);
    setLoading(false);
  };

  const loadGroups = async () => {
    const result = await getAllCustomerGroups();
    if (result.success) {
      setGroups(result.data);
    } else {
      showAlert("Failed to load customer groups", "error");
    }
  };

  const loadWhatsAppStatus = async () => {
    const result = await getAllWhatsAppStatus();
    if (result.success) {
      const statusMap = {};
      result.data.forEach(group => {
        statusMap[group.id] = group;
      });
      setWhatsappStatus(statusMap);
    }
  };

  const loadDefaultInstance = async () => {
    const result = await getDefaultInstance();
    if (result.success) {
      setDefaultInstance(result.data.defaultGroup);
    }
  };

  const showAlert = useCallback((message, severity = "info") => {
    setAlert({ show: true, message, severity });
    setTimeout(() => setAlert({ show: false, message: "", severity: "info" }), 5000);
  }, []);

  // QR Countdown timer functions
  const startQrCountdown = useCallback((duration = 20) => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }
    
    setQrMaxTimeout(duration);
    setQrCountdown(duration);
    
    countdownIntervalRef.current = setInterval(() => {
      setQrCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownIntervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const stopQrCountdown = useCallback(() => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    setQrCountdown(0);
  }, []);

  useEffect(() => {
    return () => {
      stopQrCountdown();
    };
  }, [stopQrCountdown]);

  // Socket.io event handling for QR updates
  const handleQrUpdate = useCallback((groupId, data) => {
    console.log("QR Update received:", { groupId, data });

    // Update QR data with functional state update to avoid closure issues
    setQrData(prevData => {
      if (!prevData || prevData.groupId !== groupId) {
        return prevData; // Only update if it's for the current group
      }
      return {
        ...prevData,
        qrCode: data.qrCode,
        qrRemainingTime: data.qrRemainingTime || 0
      };
    });

    // Handle countdown timing from server
    if (data.qrRemainingTime && data.qrRemainingTime > 0) {
      const countdownTime = Math.max(1, Math.min(20, data.qrRemainingTime));
      console.log("Starting QR countdown:", countdownTime);
      if (countdownTime > 0) {
        startQrCountdown(countdownTime);
      }
    }
  }, [startQrCountdown]);

  // Socket connection setup
  useEffect(() => {
    if (!socketRef.current) {
      // Use current origin in production, fallback to env variable or localhost in dev
      const socketUrl = process.env.NODE_ENV === 'production'
        ? window.location.origin
        : (process.env.REACT_APP_SOCKET_IO || 'http://localhost:5000');
      socketRef.current = io(socketUrl, {
        rejectUnauthorized: false,
        secure: process.env.NODE_ENV === 'production',
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionAttempts: 5
      });
      socketRef.current.on("qrUpdate", handleQrUpdate);
      socketRef.current.on("whatsappStatusUpdate", () => {
        loadWhatsAppStatus();
      });
      
      console.log("Socket.io connected for customer group list");
    }

    return () => {
      if (socketRef.current) {
        socketRef.current.off("qrUpdate", handleQrUpdate);
        socketRef.current.off("whatsappStatusUpdate");
        socketRef.current.disconnect();
        socketRef.current = null;
        console.log("Socket.io disconnected from customer group list");
      }
      stopQrCountdown();
    };
  }, [handleQrUpdate, stopQrCountdown]);

  const handleCreateGroup = () => {
    setEditingGroup(null);
    setFormData({
      name: "",
      whatsappAPI: "",
      whatsappInstanceName: "",
      maxConcurrentMessages: 10,
      remark: "",
      isActive: true
    });
    setOpenDialog(true);
  };

  const handleEditGroup = (group) => {
    setEditingGroup(group);
    setFormData({
      name: group.name,
      whatsappAPI: group.whatsappAPI,
      whatsappInstanceName: group.whatsappInstanceName,
      maxConcurrentMessages: group.maxConcurrentMessages || 10,
      remark: group.remark || "",
      isActive: group.isActive
    });
    setOpenDialog(true);
  };

  const handleSaveGroup = async () => {
    try {
      let result;
      if (editingGroup) {
        result = await updateCustomerGroup(editingGroup.id, formData);
      } else {
        result = await createCustomerGroup(formData);
      }

      if (result.success) {
        showAlert(
          editingGroup ? "Group updated successfully" : "Group created successfully",
          "success"
        );
        setOpenDialog(false);
        loadData();
      } else {
        showAlert(result.message, "error");
      }
    } catch (error) {
      showAlert("Operation failed", "error");
    }
  };

  const handleDeleteGroup = async (groupId) => {
    const result = await Swal.fire({
      title: 'Delete Customer Group',
      text: 'Are you sure you want to delete this group? This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#d33'
    });

    if (result.isConfirmed) {
      try {
        const deleteResult = await deleteCustomerGroup(groupId);
        if (deleteResult.success) {
          showAlert("Group deleted successfully", "success");
          loadData();
        } else {
          showAlert(deleteResult.message, "error");
        }
      } catch (error) {
        showAlert("Failed to delete group", "error");
      }
    }
  };

  const handleSetDefault = async (groupId) => {
    // Validate groupId
    if (!groupId) {
      console.error("No group ID provided");
      showAlert("Invalid group ID", "error");
      return;
    }

    const result = await Swal.fire({
      title: 'Set Default WhatsApp Instance',
      text: 'This will make this group the default instance for OTP and general messaging. Continue?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, set as default',
      cancelButtonText: 'Cancel',
      confirmButtonColor: PURPLE_MAIN
    });

    if (result.isConfirmed) {
      try {
        console.log("Setting default instance for group:", groupId);
        console.log("Group ID type:", typeof groupId, "Value:", groupId);
        
        const setResult = await setDefaultInstanceAPI(groupId);
        console.log("Set default result:", setResult);
        
        if (setResult.success) {
          showAlert(setResult.data?.message || "Default instance set successfully", "success");
          await loadData(); // Refresh all data including default instance
        } else {
          console.error("API returned error:", setResult);
          showAlert(setResult.message || "Failed to set default instance", "error");
        }
      } catch (error) {
        console.error("Error setting default instance:", error);
        console.error("Full error object:", error);
        const errorMessage = error.response?.data?.message || error.message || "Failed to set default instance";
        showAlert(`Error: ${errorMessage}`, "error");
      }
    }
  };

  const handleInitializeWhatsApp = async (groupId) => {
    if (initializingGroups[groupId]) {
      return;
    }

    setInitializingGroups(prev => ({
      ...prev,
      [groupId]: true
    }));

    try {
      const result = await initializeWhatsAppGroup(groupId);
      if (result.success) {
        showAlert("WhatsApp initialization started", "success");
        loadWhatsAppStatus();
      } else {
        showAlert(result.message, "error");
      }
    } catch (error) {
      showAlert("Failed to initialize WhatsApp instance", "error");
    } finally {
      setInitializingGroups(prev => ({
        ...prev,
        [groupId]: false
      }));
    }
  };

  // Show loading QR dialog with blurred placeholder
  const showLoadingQrDialog = useCallback((groupId) => {
    console.log("Showing loading QR dialog for group:", groupId);
    setQrLoading(true);
    setQrData({
      groupId: groupId,
      qrCode: sampleQrImage // Use sample as placeholder
    });
    setOpenQRDialog(true);
  }, []);

  const handleShowQR = async (groupId) => {
    try {
      const group = groups.find(g => g.id === groupId);
      const groupName = group ? group.name : 'Unknown';

      if (qrTimeoutRef.current) {
        clearTimeout(qrTimeoutRef.current);
        qrTimeoutRef.current = null;
      }
      stopQrCountdown();
      showLoadingQrDialog(groupId);
      qrPendingRef.current = true;

      const displayQr = (qrCode, remaining) => {
        qrPendingRef.current = false;
        if (qrTimeoutRef.current) {
          clearTimeout(qrTimeoutRef.current);
          qrTimeoutRef.current = null;
        }
        setQrData(prevData => ({
          ...prevData,
          qrCode, // Use qrCode field (dialog expects this)
          groupId: groupId,
          qrRemainingTime: remaining || 20
        }));
        startQrCountdown(Math.max(1, Math.min(90, remaining || 20)));
        setQrLoading(false);
      };

      // Set up socket listeners for this specific group BEFORE asking the backend, so a QR
      // emitted while the client (re)starts isn't missed. Later rotations also arrive here.
      if (socketRef.current) {
        const authEventName = `whatsapp-authenticated-${groupId}`;
        const qrEventName = `whatsapp-qr-${groupId}`;

        // Remove any existing listeners first
        socketRef.current.off(authEventName);
        socketRef.current.off(qrEventName);

        // Add authentication listener
        socketRef.current.on(authEventName, async (data) => {
          console.log(`✅ Authentication successful for group: ${groupName} (${groupId})`, data);
          qrPendingRef.current = false;

          // Close QR dialog
          setOpenQRDialog(false);
          setQrData(null);
          stopQrCountdown();

          // Show success message
          showAlert("WhatsApp authenticated successfully!", "success");

          // Wait a moment for the backend to fully process the authentication
          await new Promise(resolve => setTimeout(resolve, 1000));

          // Refresh status
          await loadWhatsAppStatus();

          // Clean up listeners
          socketRef.current?.off(authEventName);
          socketRef.current?.off(qrEventName);
        });

        // Add QR update listener (for real-time QR code updates via socket)
        socketRef.current.on(qrEventName, (data) => {
          console.log(`📱 QR code received via SOCKET for group: ${groupName} (${groupId})`);
          if (data && (data.qrImage || data.qr)) {
            displayQr(data.qrImage || data.qr, data.qrRemainingTime);
          }
        });
      } else {
        console.warn("Socket not connected — QR will be shown once but won't auto-refresh");
      }

      // Returns the live QR if the client is waiting for a scan; otherwise the backend
      // (re)starts the client — no client at all is why this dialog used to hang forever.
      const result = await requestGroupQR(groupId);
      if (!qrPendingRef.current) return; // QR/auth already arrived via socket, or dialog was re-opened

      if (!result.success) {
        qrPendingRef.current = false;
        setQrLoading(false);
        setOpenQRDialog(false);
        showAlert(result.message || "Failed to get a new QR code", "error");
        return;
      }

      if (result.data?.status === 'ready') {
        // Saved session was still valid — logged straight in, no QR needed.
        qrPendingRef.current = false;
        setOpenQRDialog(false);
        setQrData(null);
        setQrLoading(false);
        showAlert("WhatsApp instance is connected — no QR needed", "success");
        loadWhatsAppStatus();
        return;
      }

      if (result.data?.qrCode) {
        displayQr(result.data.qrCode, result.data.qrRemainingTime);
        loadWhatsAppStatus();
        return;
      }

      // Client is up but its first QR hasn't been emitted yet — wait for the socket.
      qrTimeoutRef.current = setTimeout(() => {
        if (qrPendingRef.current) {
          qrPendingRef.current = false;
          setQrLoading(false);
          setOpenQRDialog(false);
          showAlert("Timeout waiting for QR code. Please try again.", "error");
        }
      }, 30000);
    } catch (error) {
      console.error("❌ Error in handleShowQR:", error);
      qrPendingRef.current = false;
      setQrLoading(false);
      setOpenQRDialog(false);
      showAlert("Failed to get a QR code", "error");
    }
  };

  const handleRestartWhatsApp = async (groupId) => {
    // Check current status
    const currentStatus = whatsappStatus[groupId];
    const isAuthenticated = currentStatus?.whatsappStatus === 'ready';
    
    const result = await Swal.fire({
      title: 'Restart WhatsApp Instance',
      html: isAuthenticated 
        ? `
          <p>This instance is already authenticated.</p>
          <p><strong>Restart</strong> will reconnect using saved session (no QR needed).</p>
          <p>If you want to re-authenticate with a new QR code, use <strong>Complete Reset</strong> instead.</p>
        `
        : `
          <p>This will restart the WhatsApp instance.</p>
          <p><strong>Note:</strong> If you need a fresh QR code, use <strong>Complete Reset</strong> instead.</p>
        `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Restart',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        // Show plain loading dialog for restart. Restart usually reuses saved session.
        const loadingDialog = Swal.fire({
          title: 'Restarting WhatsApp Instance...',
          text: 'Please wait while the WhatsApp instance reconnects...',
          allowOutsideClick: false,
          showConfirmButton: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });

        const restartResult = await restartWhatsAppGroup(groupId);
        
        if (restartResult.success) {
          await loadWhatsAppStatus();
          
          // Wait a moment for the client to initialize
          await new Promise(resolve => setTimeout(resolve, 2000));
          
          // Check if the instance is now in connecting state and auto-show QR
          const status = await getAllWhatsAppStatus();
          if (status.success) {
            const groupStatus = status.data.find(s => s.id === groupId);
            if (groupStatus && groupStatus.whatsappStatus === 'connecting') {
              loadingDialog.close();
              // Auto-show QR dialog after restart
              setTimeout(() => {
                handleShowQR(groupId);
              }, 1000);
              return;
            } else if (groupStatus && groupStatus.whatsappStatus === 'ready') {
              // Already authenticated with saved session
              loadingDialog.close();
              showAlert("WhatsApp instance restarted and authenticated successfully", "success");
              return;
            }
          }
          
          loadingDialog.close();
          showAlert("WhatsApp instance restarted successfully", "success");
        } else {
          loadingDialog.close();
          showAlert(restartResult.message, "error");
        }
      } catch (error) {
        Swal.close();
        showAlert("Failed to restart WhatsApp instance", "error");
      }
    }
  };

  const handleDestroyWhatsApp = async (groupId) => {
    const result = await Swal.fire({
      title: 'Destroy WhatsApp Instance',
      text: 'Are you sure you want to destroy this WhatsApp instance? This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, destroy it!',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#d33'
    });

    if (result.isConfirmed) {
      try {
        const destroyResult = await destroyWhatsAppGroup(groupId);
        if (destroyResult.success) {
          showAlert("WhatsApp instance destroyed successfully", "success");
          loadWhatsAppStatus();
        } else {
          showAlert(destroyResult.message, "error");
        }
      } catch (error) {
        showAlert("Failed to destroy WhatsApp instance", "error");
      }
    }
  };

  // Session Management Functions
  const handleClearSessionData = async (groupId) => {
    const result = await Swal.fire({
      title: 'Clear Session Data',
      text: 'This will clear all authentication and cache data for this WhatsApp instance. The instance will need to be re-authenticated with QR code.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Clear Session Data',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#ff9800'
    });

    if (result.isConfirmed) {
      try {
        const loadingDialog = Swal.fire({
          title: 'Clearing Session Data...',
          text: 'Please wait while we clear the session data.',
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          willOpen: () => {
            Swal.showLoading();
          }
        });

        const clearResult = await clearSessionData(groupId);
        
        if (clearResult.success) {
          loadingDialog.close();
          showAlert("Session data cleared successfully. Instance needs reinitialization.", "success");
          loadWhatsAppStatus(); // Refresh the status
        } else {
          loadingDialog.close();
          showAlert(clearResult.message, "error");
        }
      } catch (error) {
        Swal.close();
        showAlert("Failed to clear session data", "error");
      }
    }
  };

  const handleCompleteReinitialization = async (groupId) => {
    const result = await Swal.fire({
      title: 'Complete Reinitialization',
      html: `
        <p><strong>This will perform a complete reset:</strong></p>
        <ul style="text-align: left; margin: 10px 0;">
          <li>Destroy the current instance</li>
          <li>Clear all session and cache data</li>
          <li>Create a fresh instance</li>
          <li>Require QR code authentication</li>
        </ul>
        <p>Use this for persistent authentication issues.</p>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Complete Reset',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#d32f2f'
    });

    if (result.isConfirmed) {
      try {
        // Get group info for better logging
        const group = groups.find(g => g.id === groupId);
        const groupName = group ? group.name : 'Unknown';
        
        // Set up QR socket listener BEFORE starting Complete Reset
        // so we don't miss the QR event when it's emitted
        console.log(`🔌 Setting up socket QR listener before Complete Reset for group: ${groupName} (${groupId})`);
        
        
        if (socketRef.current) {
          const qrEventName = `whatsapp-qr-${groupId}`;
          const authEventName = `whatsapp-authenticated-${groupId}`;
          
          // Remove any existing listeners
          socketRef.current.off(qrEventName);
          socketRef.current.off(authEventName);
          
          // Set up QR listener to capture the event during reset
          socketRef.current.on(qrEventName, (data) => {
            console.log(`📱 QR received during Complete Reset for group: ${groupName} (${groupId})`, data);
            
            if (data && (data.qrImage || data.qr)) {
              // Store QR data for when dialog opens (use qrCode field for dialog)
              const qrInfo = {
                qrCode: data.qrImage || data.qr,  // Use qrCode field (dialog expects this)
                groupId: groupId,
                qrRemainingTime: data.qrRemainingTime || 90
              };
              setQrData(qrInfo);
              qrDataRef.current = qrInfo; // Also store in ref for polling check
              setQrLoading(false);
              console.log(`✅ QR data captured and ready for display - Group: ${groupName} (${groupId})`);
            }
          });
          
          // Set up auth listener
          socketRef.current.on(authEventName, async (data) => {
            console.log(`✅ Authentication during Complete Reset for group: ${groupName} (${groupId})`, data);
            setOpenQRDialog(false);
            setQrData(null);
            stopQrCountdown();
            showAlert("WhatsApp authenticated successfully!", "success");
            
            // Wait a moment for the backend to fully process the authentication
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            // Force a fresh status load
            console.log(`🔄 Refreshing WhatsApp status after authentication for group: ${groupId}`);
            try {
              await loadWhatsAppStatus();
              console.log(`✅ Status refresh successful after authentication`);
            } catch (error) {
              console.error(`❌ Status refresh failed after authentication:`, error);
              // Show error but don't block the success message
              console.warn(`Will retry status update in next polling cycle`);
            }
            
            socketRef.current.off(authEventName);
            socketRef.current.off(qrEventName);
          });
        }
        
        const loadingDialog = Swal.fire({
          title: 'Complete Reinitialization in Progress...',
          text: 'Destroying instance, clearing data, and creating fresh instance...',
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          willOpen: () => {
            Swal.showLoading();
          }
        });

        const reinitResult = await completeReinitialization(groupId);
        
        if (reinitResult.success) {
          loadingDialog.close();
          
          // Open dialog in loading state - wait for socket QR event
          setQrLoading(true);
          setOpenQRDialog(true);
          
          showAlert("Complete reinitialization successful. Waiting for QR code...", "success");
          loadWhatsAppStatus(); // Refresh the status
          
          // Wait up to 10 seconds for QR to arrive via socket
          const qrWaitStart = Date.now();
          const maxQrWait = 10000; // 10 seconds
          
          const checkForQr = setInterval(() => {
            // Check if QR has been received using the ref (not state, as state may not update in closure)
            const currentQrData = qrDataRef.current;
            if (currentQrData && currentQrData.qrCode && currentQrData.qrCode !== sampleQrImage && currentQrData.groupId === groupId) {
              clearInterval(checkForQr);
              setQrLoading(false);
              console.log(`✅ QR received via socket, starting countdown - Group: ${groupName} (${groupId})`);
              const countdownTime = Math.max(1, Math.min(90, currentQrData.qrRemainingTime || 90));
              startQrCountdown(countdownTime);
            } else if (Date.now() - qrWaitStart > maxQrWait) {
              clearInterval(checkForQr);
              setQrLoading(false);
              console.warn(`⚠️ QR not received within timeout - Group: ${groupName} (${groupId})`);
              showAlert("QR code not generated within expected time. Please try again.", "warning");
              setOpenQRDialog(false);
            }
          }, 500); // Check every 500ms
        } else {
          loadingDialog.close();
          showAlert(reinitResult.message, "error");
        }
      } catch (error) {
        Swal.close();
        console.error('❌ Complete Reset error:', error);
        showAlert("Failed to complete reinitialization", "error");
      }
    }
  };

  const handleRecoverAuthentication = async (groupId) => {
    const result = await Swal.fire({
      title: 'Recover Authentication',
      text: 'This will attempt to recover the authentication state for this WhatsApp instance. Use this when the client appears connected but messages fail.',
      icon: 'info',
      showCancelButton: true,
      confirmButtonText: 'Recover Authentication',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#2196f3'
    });

    if (result.isConfirmed) {
      try {
        const loadingDialog = Swal.fire({
          title: 'Recovering Authentication...',
          text: 'Please wait while we attempt to recover the authentication state.',
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          willOpen: () => {
            Swal.showLoading();
          }
        });

        const recoverResult = await recoverAuthentication(groupId);
        
        if (recoverResult.success) {
          loadingDialog.close();
          showAlert("Authentication recovery initiated successfully.", "success");
          loadWhatsAppStatus(); // Refresh the status
        } else {
          loadingDialog.close();
          showAlert(recoverResult.message, "error");
        }
      } catch (error) {
        Swal.close();
        showAlert("Failed to recover authentication", "error");
      }
    }
  };

  const handleForceReinitialize = async (groupId) => {
    const result = await Swal.fire({
      title: 'Force Reinitialize',
      text: 'This will destroy and recreate the WhatsApp instance without clearing session data. Use this for general connection issues.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Force Reinitialize',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#ff5722'
    });

    if (result.isConfirmed) {
      try {
        const loadingDialog = Swal.fire({
          title: 'Force Reinitialization in Progress...',
          text: 'Destroying and recreating the WhatsApp instance...',
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          willOpen: () => {
            Swal.showLoading();
          }
        });

        const reinitResult = await forceReinitialize(groupId);
        
        if (reinitResult.success) {
          loadingDialog.close();
          showAlert("Force reinitialization completed successfully.", "success");
          loadWhatsAppStatus(); // Refresh the status
        } else {
          loadingDialog.close();
          showAlert(reinitResult.message, "error");
        }
      } catch (error) {
        Swal.close();
        showAlert("Failed to force reinitialize", "error");
      }
    }
  };

  const handleSendMessage = async () => {
    if (sendingMessage) return;

    // Debug logging
    console.log("Send message clicked:", {
      groupId: messageData.groupId,
      phoneNumber: messageData.phoneNumber,
      message: messageData.message
    });

    // More specific validation with detailed error messages
    if (!messageData.groupId) {
      console.error("No groupId found:", messageData.groupId);
      Swal.fire("Validation Error", "No customer group selected. Please try reopening the dialog.", "error");
      return;
    }
    if (!messageData.phoneNumber || messageData.phoneNumber.trim() === '') {
      Swal.fire("Validation Error", "Please enter a phone number", "error");
      return;
    }
    if (!messageData.message || messageData.message.trim() === '') {
      Swal.fire("Validation Error", "Please enter a message", "error");
      return;
    }

    // Format phone number (remove spaces, dashes, and ensure proper format)
    const cleanPhoneNumber = messageData.phoneNumber.replace(/[^\d+]/g, '');
    console.log("Sending to cleaned phone number:", cleanPhoneNumber);

    try {
      setSendingMessage(true);

      const result = await sendWhatsAppGroupMessage(
        messageData.groupId,
        cleanPhoneNumber,
        messageData.message
      );

      console.log("Send message result:", result);

      if (result.success) {
        await Swal.fire({
          title: "Success",
          text: `Message queued successfully${result.data?.jobId ? ` (Job ID: ${result.data.jobId})` : ""}`,
          icon: "success",
          timer: 1500, // Auto-close after 1.5 seconds
          timerProgressBar: true
        });
        
        // Close the message dialog
        setOpenMessageDialog(false);
        setMessageData({ groupId: null, phoneNumber: "", message: "" });
      } else {
        Swal.fire("Error", result.message || "Failed to queue message", "error");
      }
    } catch (error) {
      console.error("Send message error:", error);
      Swal.fire("Error", `Error sending message: ${error.message}`, "error");
    } finally {
      setSendingMessage(false);
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedGroups.length === 0) {
      showAlert("Please select groups first", "error");
      return;
    }

    const result = await bulkUpdateCustomerGroups(selectedGroups, action);
    if (result.success) {
      showAlert(`Bulk ${action} completed successfully`, "success");
      setSelectedGroups([]);
      loadData();
    } else {
      showAlert(result.message, "error");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ready': return 'success';
      case 'connecting': return 'warning';
      case 'disconnected': return 'error';
      case 'auth_failure': return 'error';
      case 'init_error': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'ready': return <WhatsAppIcon />;
      case 'connecting': return <CircularProgress size={16} />;
      case 'disconnected': return <StopIcon />;
      default: return <WhatsAppIcon />;
    }
  };

  const handleMenuClick = (event, groupId) => {
    setAnchorEl(event.currentTarget);
    setSelectedMenuGroupId(groupId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedMenuGroupId(null);
  };

  const handleOpenMessageDialog = (groupId) => {
    console.log("Opening message dialog for group:", groupId);
    setMessageData(prev => ({ ...prev, groupId, phoneNumber: "", message: "" }));
    setOpenMessageDialog(true);
    handleMenuClose();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Alert */}
      {alert.show && (
        <Alert severity={alert.severity} sx={{ mb: 2 }}>
          {alert.message}
        </Alert>
      )}

      {/* Default Instance Status */}
      {defaultInstance && (
        <Card sx={{ mb: 3, background: BG_PURPLE_GRADIENT, border: `1px solid ${BORDER_PURPLE}`, borderRadius: RADIUS_CARD, boxShadow: CARD_SHADOW, color: TEXT_BODY }}>
          <CardContent sx={{ py: 2 }}>
            <Box display="flex" alignItems="center" gap={2}>
              <StarIcon sx={{ color: PURPLE_MAIN }} />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: PURPLE_DARK }}>
                  Default WhatsApp Instance for OTP
                </Typography>
                <Typography variant="body2">
                  {defaultInstance.name} • Status: {whatsappStatus[defaultInstance.id]?.whatsappStatus || 'Unknown'}
                  {(whatsappStatus[defaultInstance.id]?.whatsappPhoneNumber || defaultInstance.whatsappPhoneNumber) &&
                    ` • Phone: ${whatsappStatus[defaultInstance.id]?.whatsappPhoneNumber || defaultInstance.whatsappPhoneNumber}`}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Header Actions */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" gap={2}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateGroup}
            // sx={{ backgroundColor: theme.palette.primary.dark }}
            color="secondary"
          >
            New Group
          </Button>
          <Button
            color="secondary"
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadData}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<MessageIcon />}
            onClick={() => navigate('/whatsappbulk')}
            color="success"
            sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#1DA851' } }}
          >
            Bulk Messaging
          </Button>
        </Box>

        {selectedGroups.length > 0 && (
          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => handleBulkAction('activate')}
              color="success"
            >
              Activate ({selectedGroups.length})
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => handleBulkAction('deactivate')}
              color="warning"
            >
              Deactivate ({selectedGroups.length})
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => handleBulkAction('delete')}
              color="error"
            >
              Delete ({selectedGroups.length})
            </Button>
          </Box>
        )}
      </Box>

      {/* Groups Grid */}
      <Grid container spacing={3}>
        {groups.map((group) => {
          const status = whatsappStatus[group.id];
          const whatsappState = status?.whatsappStatus || 'not_initialized';
          const instanceActive = status?.instanceStatus?.isActive || false;

          return (
            <Grid item xs={12} sm={6} md={4} key={group.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  background: BG_WHITE,
                  borderRadius: RADIUS_CARD,
                  boxShadow: CARD_SHADOW,
                  border: selectedGroups.includes(group.id) ? `2px solid ${PURPLE_MAIN}` : `1px solid ${BORDER}`,
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  {/* Header with checkbox and menu */}
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Box display="flex" alignItems="center">
                      <Checkbox
                        color="secondary"
                        checked={selectedGroups.includes(group.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedGroups([...selectedGroups, group.id]);
                          } else {
                            setSelectedGroups(selectedGroups.filter(id => id !== group.id));
                          }
                        }}
                        size="small"
                      />
                      <GroupIcon sx={{ mr: 1 }} color="secondary" />
                    </Box>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuClick(e, group.id)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Box>

                  {/* Group Info */}
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                      {group.name}
                    </Typography>
                    {/* Default Instance Badge */}
                    {defaultInstance?.id === group.id && (
                      <Chip
                        label="DEFAULT"
                        size="small"
                        color="secondary"
                        variant="filled"
                        sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}
                      />
                    )}
                  </Box>

                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    API: {group.whatsappAPI}
                  </Typography>

                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Instance: {group.whatsappInstanceName}
                  </Typography>

                  {status?.whatsappPhoneNumber && (
                    <Box display="flex" alignItems="center" mb={1}>
                      <PhoneIcon sx={{ fontSize: 16, mr: 1 }} />
                      <Typography variant="body2">
                        {status.whatsappPhoneNumber}
                      </Typography>
                    </Box>
                  )}

                  {/* Status Chips */}
                  <Box display="flex" gap={1} mb={2} flexWrap="wrap">
                    <Chip
                      icon={getStatusIcon(whatsappState)}
                      label={whatsappState.replace('_', ' ').toUpperCase()}
                      color={getStatusColor(whatsappState)}
                      size="small"
                    />
                    <Chip
                      label={group.isActive ? 'ACTIVE' : 'INACTIVE'}
                      color={group.isActive ? 'success' : 'default'}
                      size="small"
                    />
                    {instanceActive && (
                      <Chip
                        label="INSTANCE RUNNING"
                        color="info"
                        size="small"
                      />
                    )}
                  </Box>

                  {/* Stats */}
                  <Box display="flex" justifyContent="space-between" mb={2}>
                    <Box textAlign="center">
                      <Typography variant="body2" color="textSecondary">
                        Queue
                      </Typography>
                      <Typography variant="h6">
                        {status?.messageQueue || 0}
                      </Typography>
                    </Box>
                    <Box textAlign="center">
                      <Typography variant="body2" color="textSecondary">
                        Max Messages
                      </Typography>
                      <Typography variant="h6">
                        {group.maxConcurrentMessages || 10}
                      </Typography>
                    </Box>
                  </Box>

                  {group.remark && (
                    <Typography variant="body2" color="textSecondary" style={{ fontStyle: 'italic' }}>
                      {group.remark}
                    </Typography>
                  )}

                  {status?.lastActivity && (
                    <Typography variant="caption" color="textSecondary" display="block" mt={1}>
                      Last Activity: {new Date(status.lastActivity).toLocaleString()}
                    </Typography>
                  )}
                </CardContent>

                <CardActions>
                  <Button
                    variant="outlined"
                    color="secondary"
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => handleEditGroup(group)}
                  >
                    Edit
                  </Button>

                  <Button
                    variant="outlined"
                    color="secondary"
                    size="small"
                    startIcon={<ChatIcon />}
                    onClick={() => navigate(`/whatsapp-chat/${group.id}`)}
                  >
                    Open Chat
                  </Button>

                  {whatsappState === 'not_initialized' && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={initializingGroups[group.id] ? <CircularProgress size={16} color="inherit" /> : <PlayIcon />}
                      onClick={() => handleInitializeWhatsApp(group.id)}
                      color="secondary"
                      disabled={Boolean(initializingGroups[group.id])}
                    >
                      {initializingGroups[group.id] ? 'Initializing...' : 'Initialize'}
                    </Button>
                  )}

                  {['connecting', 'disconnected', 'error', 'auth_failure'].includes(whatsappState) && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<QrCodeIcon />}
                      onClick={() => handleShowQR(group.id)}
                      color="info"
                    >
                      QR Code
                    </Button>
                  )}

                  {whatsappState === 'ready' && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<MessageIcon />}
                      onClick={() => handleOpenMessageDialog(group.id)}
                      color="success"
                    >
                      Message
                    </Button>
                  )}
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          const group = groups.find(g => g.id === selectedMenuGroupId);
          handleEditGroup(group);
          handleMenuClose();
        }}>
          <EditIcon sx={{ mr: 1 }} /> Edit Group
        </MenuItem>

        {whatsappStatus[selectedMenuGroupId]?.whatsappStatus === 'ready' && (
          <MenuItem onClick={() => handleOpenMessageDialog(selectedMenuGroupId)}>
            <MessageIcon sx={{ mr: 1 }} /> Send Message
          </MenuItem>
        )}

        {/* Set as Default - only show if not already default */}
        {defaultInstance?.id !== selectedMenuGroupId && (
          <MenuItem onClick={() => {
            handleSetDefault(selectedMenuGroupId);
            handleMenuClose();
          }} sx={{ color: 'secondary.main' }}>
            <WhatsAppIcon sx={{ mr: 1 }} /> Set as Default for OTP
          </MenuItem>
        )}

        {whatsappStatus[selectedMenuGroupId]?.whatsappStatus === 'connecting' && (
          <MenuItem onClick={() => {
            handleShowQR(selectedMenuGroupId);
            handleMenuClose();
          }}>
            <QrCodeIcon sx={{ mr: 1 }} /> Show QR Code
          </MenuItem>
        )}

        <MenuItem onClick={() => {
          handleRestartWhatsApp(selectedMenuGroupId);
          handleMenuClose();
        }}>
          <RefreshIcon sx={{ mr: 1 }} /> Restart WhatsApp
        </MenuItem>

        {/* Session Management Options */}
        <MenuItem onClick={() => {
          handleRecoverAuthentication(selectedMenuGroupId);
          handleMenuClose();
        }}>
          <SecurityIcon sx={{ mr: 1 }} /> Recover Authentication
        </MenuItem>

        <MenuItem onClick={() => {
          handleForceReinitialize(selectedMenuGroupId);
          handleMenuClose();
        }}>
          <PowerIcon sx={{ mr: 1 }} /> Force Reinitialize
        </MenuItem>

        <MenuItem onClick={() => {
          handleClearSessionData(selectedMenuGroupId);
          handleMenuClose();
        }}>
          <ClearIcon sx={{ mr: 1 }} /> Clear Session Data
        </MenuItem>

        <MenuItem onClick={() => {
          handleCompleteReinitialization(selectedMenuGroupId);
          handleMenuClose();
        }} sx={{ color: 'warning.main' }}>
          <RestoreIcon sx={{ mr: 1 }} /> Complete Reset
        </MenuItem>

        <MenuItem onClick={() => {
          handleDestroyWhatsApp(selectedMenuGroupId);
          handleMenuClose();
        }}>
          <StopIcon sx={{ mr: 1 }} /> Destroy Instance
        </MenuItem>

        <MenuItem onClick={() => {
          handleDeleteGroup(selectedMenuGroupId);
          handleMenuClose();
        }} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} /> Delete Group
        </MenuItem>
      </Menu>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingGroup ? 'Edit Customer Group' : 'Create New Customer Group'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Group Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="WhatsApp API"
            value={formData.whatsappAPI}
            onChange={(e) => setFormData({ ...formData, whatsappAPI: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Instance Name"
            value={formData.whatsappInstanceName}
            onChange={(e) => setFormData({ ...formData, whatsappInstanceName: e.target.value })}
            margin="normal"
            required
            helperText="Unique identifier for the WhatsApp session"
          />
          <TextField
            fullWidth
            label="Max Concurrent Messages"
            type="number"
            value={formData.maxConcurrentMessages}
            onChange={(e) => setFormData({ ...formData, maxConcurrentMessages: parseInt(e.target.value) })}
            margin="normal"
            inputProps={{ min: 1, max: 100 }}
          />
          <TextField
            fullWidth
            label="Remark"
            value={formData.remark}
            onChange={(e) => setFormData({ ...formData, remark: e.target.value })}
            margin="normal"
            multiline
            rows={2}
          />
          <FormControlLabel
            control={
              <Switch
                color="secondary"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              />
            }
            label="Active"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Box px={2} py={1} gap={1} display="flex" justifyContent="flex-end">
            <Button onClick={() => setOpenDialog(false)} color="secondary" variant="outlined">Cancel</Button>
            <Button onClick={handleSaveGroup} variant="contained" color="secondary">
              {editingGroup ? 'Update' : 'Create'}
            </Button>
          </Box>

        </DialogActions>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={openQRDialog} onClose={() => {
        // Clean up authentication listener when dialog is closed
        if (qrData && qrData.groupId && socketRef.current) {
          const authEventName = `whatsapp-authenticated-${qrData.groupId}`;
          socketRef.current.off(authEventName);
          console.log(`Cleaned up authentication listener for group ${qrData.groupId}`);
        }
        qrPendingRef.current = false;
        setOpenQRDialog(false);
        setQrData(null);
        stopQrCountdown();
      }} maxWidth="sm">
        <DialogTitle>
          Scan QR Code with WhatsApp
          {qrCountdown > 0 && (
            <Typography variant="subtitle2" color="textSecondary">
              QR expires in {qrCountdown} seconds
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', p: 3 }}>
          {/* Progress bar for countdown */}
          {qrCountdown > 0 && (
            <LinearProgress 
              variant="determinate" 
              value={(qrCountdown / qrMaxTimeout) * 100} 
              sx={{ mb: 2, height: 6, borderRadius: 3 }}
            />
          )}
          
          {qrData ? (
            <>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  mb: 2
                }}
              >
                <img
                  src={qrData.qrCode || sampleQrImage}
                  alt="WhatsApp QR Code"
                  style={{
                    width: '280px',
                    height: '280px',
                    objectFit: 'contain',
                    filter: qrLoading || !qrData.qrCode ? 'blur(4px)' : 'none',
                    transition: 'filter 0.3s ease'
                  }}
                />
              </Box>
              
              {qrLoading && (
                <Box sx={{ mb: 2 }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    Generating new QR code...
                  </Typography>
                </Box>
              )}
              
              <Typography variant="body2" color="textSecondary">
                Group: {groups.find(g => g.id === qrData.groupId)?.name || qrData.groupId}
              </Typography>
              
              {qrCountdown > 0 && (
                <Typography variant="caption" color="secondary" sx={{ mt: 1, display: 'block' }}>
                  Scan quickly! QR expires in {qrCountdown} seconds
                </Typography>
              )}

              {!qrLoading && qrCountdown === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  QR code expired? A new one normally appears automatically — if not, click "Get new QR".
                </Typography>
              )}
            </>
          ) : (
            <Box>
              <img
                src={sampleQrImage}
                alt="Loading QR Code"
                style={{
                  width: '280px',
                  height: '280px',
                  objectFit: 'contain',
                  filter: 'blur(4px)'
                }}
              />
              <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                Loading QR code...
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant={!qrLoading && qrCountdown === 0 ? "contained" : "outlined"}
            color="secondary"
            startIcon={<RefreshIcon />}
            disabled={qrLoading || !qrData?.groupId}
            onClick={() => handleShowQR(qrData.groupId)}
          >
            Get new QR
          </Button>
          <Button variant="outlined" color="secondary" onClick={() => {
            qrPendingRef.current = false;
            setOpenQRDialog(false);
            stopQrCountdown();
          }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send Message Dialog */}
      <Dialog open={openMessageDialog} onClose={() => {
        if (sendingMessage) return;
        setOpenMessageDialog(false);
        setMessageData({ groupId: null, phoneNumber: "", message: "" });
      }} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box>
            <Typography variant="h6" component="div">
              Send Test Message
            </Typography>
            {messageData.groupId && (
              <Typography variant="subtitle2" color="textSecondary" component="div">
                Group: {groups.find(g => g.id === messageData.groupId)?.name || 'Unknown'}
              </Typography>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {sendingMessage && <LinearProgress sx={{ mt: 1, mb: 2 }} />}
          <TextField
            fullWidth
            label="Phone Number"
            value={messageData.phoneNumber}
            onChange={(e) => setMessageData({ ...messageData, phoneNumber: e.target.value })}
            margin="normal"
            required
            placeholder="+1234567890"
            helperText="Include country code"
            disabled={sendingMessage}
          />
          <TextField
            fullWidth
            label="Message"
            value={messageData.message}
            onChange={(e) => setMessageData({ ...messageData, message: e.target.value })}
            margin="normal"
            required
            multiline
            rows={4}
            placeholder="Enter your message here..."
            disabled={sendingMessage}
          />
        </DialogContent>
        <DialogActions>
          <Box px={2} py={1} gap={1} display="flex" justifyContent="flex-end">
            <Button color="secondary" variant="outlined" onClick={() => {
              if (sendingMessage) return;
              setOpenMessageDialog(false);
              setMessageData({ groupId: null, phoneNumber: "", message: "" });
            }} disabled={sendingMessage}>Cancel</Button>
            <Button
              onClick={handleSendMessage}
              variant="contained"
              color="secondary"
              disabled={sendingMessage || !messageData.groupId || !messageData.phoneNumber.trim() || !messageData.message.trim()}
            >
              {sendingMessage ? (
                <Box display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={16} color="inherit" />
                  Sending...
                </Box>
              ) : (
                "Send Message"
              )}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WhatsAppInstance;